/* eslint-disable max-len */
import { Col, message, Row } from 'antd';
import { Formik } from 'formik';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import * as permissionKind from '../../../../../shared/permissions';
import {
    type FinderApplicationPrivateModuleInDealerSpecsFragment,
    type FinderApplicationPublicModuleInDealerSpecsFragment,
} from '../../../../api/fragments';
import { type ApplicationMarketTypeFragmentFragment } from '../../../../api/fragments/ApplicationMarketTypeFragment';
import { type DealerWithPermissionsFragmentFragment } from '../../../../api/fragments/DealerWithPermissionsFragment';
import { useUpdateApplicationDealershipAssignmentsByDealerMutation } from '../../../../api/mutations/updateApplicationDealershipAssignmentsByDealer';
import {
    type UpdateFinderApplicationPrivateModuleEmailContentsMutationVariables,
    useUpdateFinderApplicationPrivateModuleEmailContentsMutation,
} from '../../../../api/mutations/updateFinderApplicationPrivateModuleEmailContents';
import {
    type UpdateFinderApplicationPublicModuleEmailContentsMutationVariables,
    useUpdateFinderApplicationPublicModuleEmailContentsMutation,
} from '../../../../api/mutations/updateFinderApplicationPublicModuleEmailContents';
import {
    ContentRefinementSourceAction,
    ContentRefinementSourceKind,
    EmailContentUpdateType,
    type TranslatedStringInput,
} from '../../../../api/types';
import { GenericStateOnPageContextProvider } from '../../../../components/contexts/GenericStateOnPageContextManager';
import Form from '../../../../components/fields/Form';
import PriceDisclaimerField from '../../../../components/fields/PriceDisclaimerField';
import TranslatedMarkdownField from '../../../../components/fields/TranslatedMarkdownField';
import CollapsibleWrapper, { Panel } from '../../../../components/wrappers/CollapsibleWrapper';
import FormFields from '../../../../themes/admin/Fields/FormFields';
import hasPermissions from '../../../../utilities/hasPermissions';
import useHandleError from '../../../../utilities/useHandleError';
import useMarkDownInfoTooltip from '../../../../utilities/useMarkDownInfoTooltip';
import useValidator from '../../../../utilities/useValidator';
import FinderApplicationModuleEmailForm from '../../ModuleDetailsPage/FinderApplicationModulePage/FinderApplicationModuleEmailForm';
import useHandleFinderApplicationModuleAssets from '../../ModuleDetailsPage/FinderApplicationModulePage/useHandleFinderApplicationModuleAssets';
import useSalesPersonOptions from '../../ModuleDetailsPage/modules/implementations/shared/useSalesPersonOptions';
import FinanceProductAssignmentSection from './FinanceProductAssignmentSection';
import InsuranceProductAssignmentSection from './InsuranceProductAssignmentSection';
import MarketTypeEditableField, {
    type DealerMarketType,
    dealerMarketTypeValidations,
    getInitDealerMarketType,
    pickDealerMarketType,
} from './MarketTypeEditableField';

export type DealerAssignmentFinderFormProps = {
    module: FinderApplicationPublicModuleInDealerSpecsFragment | FinderApplicationPrivateModuleInDealerSpecsFragment;
    dealer: DealerWithPermissionsFragmentFragment;
};

type FormValues = {
    dealerFinanceProductSuiteIds: string[];
    dealerAssignee: string;
    dealerFinancingDisclaimer: TranslatedStringInput[];
    dealerPriceDisclaimer: TranslatedStringInput[];
    dealerMarketType: DealerMarketType;
    emailContents: DealerAssignmentFinderFormProps['module']['emailContents'];
    dealerReservationInstructions: TranslatedStringInput;
    dealerInsuranceProductSuiteIds: string[];
};

const createValidations = dealerMarketTypeValidations;
const DealerAssignmentFinderForm = ({ module, dealer }: DealerAssignmentFinderFormProps) => {
    const { t } = useTranslation(['dealershipManagement', 'finderApplicationModuleDetails', 'moduleDetails']);
    const [mutation] = useUpdateApplicationDealershipAssignmentsByDealerMutation();
    const [updatePublicModuleEmailContentMutation] = useUpdateFinderApplicationPublicModuleEmailContentsMutation();
    const [updatePrivateModuleEmailContentMutation] = useUpdateFinderApplicationPrivateModuleEmailContentsMutation();

    const handleFinderApplicationModuleAssets = useHandleFinderApplicationModuleAssets();

    const { reservationPeriod } = module;

    const marketType = module.marketType as ApplicationMarketTypeFragmentFragment;

    const refineContentParams = useMemo(
        () => ({
            hasPermissionToRefineContent: hasPermissions(dealer.permissions, [permissionKind.updateDealer]),
            source: {
                kind: ContentRefinementSourceKind.Module,
                action: ContentRefinementSourceAction.Update,
                identifier: module.id,
            },
        }),
        [dealer.permissions, module.id]
    );

    // initialize finance products and dealers for the dealer
    const initialValues: FormValues = useMemo(
        () => ({
            dealerFinanceProductSuiteIds:
                module.dealerFinanceProducts.find(({ dealerId }) => dealerId === dealer.id)?.financeProductSuiteIds ||
                [],
            dealerInsuranceProductSuiteIds:
                module.dealerInsuranceProducts.find(({ dealerId }) => dealerId === dealer.id)
                    ?.insuranceProductSuiteIds || [],
            ...(module.__typename === 'FinderApplicationPublicModule'
                ? {
                      dealerAssignee: module.assignee.overrides.find(({ dealerId }) => dealerId === dealer.id)
                          ?.relatedId,
                  }
                : null),
            dealerPriceDisclaimer:
                module.priceDisclaimer.overrides.find(({ dealerId }) => dealerId === dealer.id)?.value ?? [],
            dealerFinancingDisclaimer:
                module.financingDisclaimer.overrides.find(({ dealerId }) => dealerId === dealer.id)?.value ?? [],
            dealerMarketType: getInitDealerMarketType(marketType, dealer.id),
            emailContents: module.emailContents,
            dealerReservationInstructions: module.reservationInstructions.overrides.find(
                ({ dealerId }) => dealerId === dealer.id
            )?.value ?? { defaultValue: '', overrides: [] },
        }),
        [dealer.id, module, marketType]
    );

    const { salesPersonOptions, loading } = useSalesPersonOptions(dealer.company, true);

    const validator = useMemo(() => createValidations(marketType), [marketType]);
    const validate = useValidator(validator);

    const onSubmit = useHandleError<FormValues>(
        async values => {
            const {
                dealerFinanceProductSuiteIds,
                dealerAssignee,
                dealerPriceDisclaimer,
                dealerFinancingDisclaimer,
                dealerMarketType,
                dealerReservationInstructions,
                emailContents,
                dealerInsuranceProductSuiteIds,
            } = values;

            const { submitOrder, reminderEmail } = emailContents;
            const { introImage: submitOrderIntroImage, ...submitOrderSetting } = submitOrder;
            const { introImage: reminderEmailIntroImage, ...reminderEmailSetting } = reminderEmail;

            // submitting message
            message.loading({
                content: t('dealershipManagement:messages.updateSubmitting'),
                key: 'primary',
                duration: 0,
            });

            // todo: implement after confirming the feature
            const dealerVehicles = { dealerId: dealer.id, vehicleSuiteIds: [] };
            const dealerFinanceProducts = { dealerId: dealer.id, financeProductSuiteIds: dealerFinanceProductSuiteIds };
            const dealerInsuranceProducts = {
                dealerId: dealer.id,
                insuranceProductSuiteIds: dealerInsuranceProductSuiteIds,
            };
            const variables = {
                moduleId: module.id,
                dealerVehicles,
                dealerFinanceProducts,
                dealerInsuranceProducts,
                dealerAssignee: {
                    dealerId: dealer.id,
                    relatedId: dealerAssignee,
                },
                ...(dealerPriceDisclaimer && {
                    dealerPriceDisclaimer: {
                        dealerId: dealer.id,
                        value: dealerPriceDisclaimer,
                    },
                }),
                ...(dealerFinancingDisclaimer && {
                    dealerFinancingDisclaimer: {
                        dealerId: dealer.id,
                        value: dealerFinancingDisclaimer,
                    },
                }),
                ...(dealerReservationInstructions && {
                    dealerReservationInstructions: {
                        dealerId: dealer.id,
                        value: dealerReservationInstructions,
                    },
                }),
                dealerMarketType: {
                    dealerId: dealer.id,
                    ...pickDealerMarketType(marketType, dealerMarketType),
                },
            };

            const updateFinderEmailContentVariables:
                | UpdateFinderApplicationPublicModuleEmailContentsMutationVariables
                | UpdateFinderApplicationPrivateModuleEmailContentsMutationVariables = {
                moduleId: module.id,
                settings: {
                    emailContentUpdateType: EmailContentUpdateType.Dealer,
                    dealerId: dealer.id,
                    submitOrder: submitOrderSetting,
                    reminderEmail: reminderEmailSetting,
                },
            };

            const updateFunction =
                module.__typename === 'FinderApplicationPrivateModule'
                    ? updatePrivateModuleEmailContentMutation
                    : updatePublicModuleEmailContentMutation;

            await mutation({ variables })
                .then(() => updateFunction({ variables: updateFinderEmailContentVariables }))
                .finally(() => {
                    message.destroy('primary');
                });

            await handleFinderApplicationModuleAssets(
                module.id,
                { submitOrder, reminderEmail },
                EmailContentUpdateType.Dealer,
                module.emailContents,
                dealer.id
            );

            // show successful message
            message.success({
                content: t('dealershipManagement:messages.updateSuccessful'),
                key: 'primary',
            });
        },
        [
            t,
            dealer.id,
            module.id,
            module.__typename,
            module.emailContents,
            marketType,
            updatePrivateModuleEmailContentMutation,
            updatePublicModuleEmailContentMutation,
            mutation,
            handleFinderApplicationModuleAssets,
        ]
    );

    const formId = `dealerForm-${module.id}`;

    const markdownInfoTooltip = useMarkDownInfoTooltip(marketType.type);
    const hasUpdatePermission = hasPermissions(dealer.permissions, [permissionKind.updateDealer]);

    return (
        <GenericStateOnPageContextProvider refineContent={refineContentParams}>
            <Formik<FormValues>
                initialValues={initialValues}
                onSubmit={onSubmit}
                validate={validate}
                enableReinitialize
            >
                {({ handleSubmit }) => (
                    <Form id={formId} name={formId} onSubmitCapture={handleSubmit}>
                        <CollapsibleWrapper defaultActiveKey={['configuration']}>
                            <Panel
                                key="configuration"
                                header={t('finderApplicationModuleDetails:subTitles.configuration')}
                            >
                                <Row gutter={10}>
                                    {module.__typename === 'FinderApplicationPublicModule' && (
                                        <Col md={8} xs={24}>
                                            <FormFields.SelectField
                                                {...t('finderApplicationModuleDetails:fields.assignee', {
                                                    returnObjects: true,
                                                })}
                                                disabled={!hasUpdatePermission}
                                                loading={loading}
                                                name="dealerAssignee"
                                                options={salesPersonOptions}
                                                showSearch
                                            />
                                        </Col>
                                    )}

                                    <Col md={8} xs={24}>
                                        <TranslatedMarkdownField
                                            {...t('moduleDetails:fields.reservationInstructions', {
                                                returnObjects: true,
                                            })}
                                            disabled={!hasUpdatePermission}
                                            name="dealerReservationInstructions"
                                        />
                                    </Col>

                                    <Col md={8} xs={24}>
                                        <PriceDisclaimerField
                                            {...t('moduleDetails:fields.priceDisclaimerTitle', { returnObjects: true })}
                                            disabled={!hasUpdatePermission}
                                            name="dealerPriceDisclaimer"
                                            tooltip={markdownInfoTooltip}
                                        />
                                    </Col>
                                    <MarketTypeEditableField
                                        disabled={!hasUpdatePermission}
                                        marketType={module.marketType}
                                    />
                                </Row>
                            </Panel>
                        </CollapsibleWrapper>
                        <FinanceProductAssignmentSection
                            disabled={!hasUpdatePermission}
                            moduleId={module.bankModuleId}
                        />
                        {module.insuranceModuleId && (
                            <InsuranceProductAssignmentSection
                                disabled={!hasUpdatePermission}
                                moduleId={module.insuranceModuleId}
                            />
                        )}

                        <CollapsibleWrapper defaultActiveKey={['email']}>
                            <Panel key="email" header={t('finderApplicationModuleDetails:tabs.email')}>
                                <FinderApplicationModuleEmailForm
                                    companyId={module.company.id}
                                    disabled={!hasUpdatePermission}
                                    prefix="emailContents"
                                    reservationPeriod={reservationPeriod}
                                    showUpdateButton={false}
                                    targetDealerId={dealer.id}
                                    defaultValueDisabled
                                />
                            </Panel>
                        </CollapsibleWrapper>
                    </Form>
                )}
            </Formik>
        </GenericStateOnPageContextProvider>
    );
};

export default DealerAssignmentFinderForm;
